import type { Channel } from 'phoenix';
import { Delta } from 'quill/core';
import type { Dispatch, SetStateAction } from 'react';
import type { AiAskStreamResult } from './SearchView';
import { PostType } from '../graphql/generated/graphql';
import { isFeatureEnabled } from '../utils/features';

const consoleInfo = (...args: unknown[]) => {
  if (isFeatureEnabled('devtools')) {
    // eslint-disable-next-line no-console
    console.info(...args);
  }
};

const attachAiAskV2 = (
  channel: Channel,
  setValue: Dispatch<
    SetStateAction<{
      loading: boolean;
      response: AiAskStreamResult | null;
    }>
  >,
): void => {
  let answerDelta = new Delta();
  let answerText = '';

  channel.on('start', (payload) => {
    answerDelta = new Delta();
    answerText = '';
    consoleInfo('start', payload);

    setValue(
      (oldValue: { loading: boolean; response: AiAskStreamResult | null }) => {
        if (!oldValue.loading) return oldValue;
        return {
          response: { threadId: payload.response_id, answer: '', sources: [] },
          loading: true,
        };
      },
    );
  });

  channel.on('answer_text', (payload) => {
    answerText += payload.text;
    answerDelta = answerDelta.insert(payload.text);
    consoleInfo('answer_text', payload);

    setValue((oldValue) => {
      if (
        !oldValue.response ||
        oldValue.response.threadId !== payload.response_id
      ) {
        return oldValue;
      }
      return {
        response: {
          threadId: payload.response_id,
          answer: oldValue.response.answer + payload.text,
          sources: [],
        },
        loading: false,
      };
    });
  });

  channel.on('search', (payload) => {
    consoleInfo('search', payload);
  });

  channel.on('answer_delta', ({ delta }) => {
    answerDelta = answerDelta.compose(new Delta(delta));
    consoleInfo('answer_delta', [delta, answerText, answerDelta]);
  });

  channel.on('response', (payload) => {
    consoleInfo('response', payload);

    setValue((oldValue) => {
      if (
        !oldValue.response ||
        oldValue.response.threadId !== payload.response_id
      ) {
        return oldValue;
      }
      return {
        response: {
          threadId: payload.response_id,
          answer: payload.text,
          sources: payload.sources.map(
            (source: { id: string; primary: string; snippet: string }) => ({
              ...source,
              type: PostType.Post,
            }),
          ),
        },
        loading: false,
      };
    });
  });
};

export default attachAiAskV2;
